import { useEffect, useState } from "react";
import api, { fmtMoney } from "@/lib/api";
import { PageHeader, Loader, StatCard } from "@/components/ui-kit";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users2, Target, FileText, FileSignature, TrendingUp, Banknote, FolderKanban, CheckCircle2, AlertTriangle, Clock, Plane, Timer, Download } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Reports() {
  const [comm, setComm] = useState(null);
  const [proj, setProj] = useState(null);
  const [collab, setCollab] = useState(null);
  const [time, setTime] = useState(null);
  const [period, setPeriod] = useState("all");

  useEffect(() => {
    api.get("/reports/time").then((r) => setTime(r.data));
  }, []);

  useEffect(() => {
    const now = new Date(), y = now.getFullYear();
    let s = null, e = null;
    if (period === "month") { s = new Date(y, now.getMonth(), 1); e = new Date(y, now.getMonth() + 1, 0); }
    else if (period === "quarter") { const q = Math.floor(now.getMonth() / 3); s = new Date(y, q * 3, 1); e = new Date(y, q * 3 + 3, 0); }
    else if (period === "year") { s = new Date(y, 0, 1); e = new Date(y, 11, 31); }
    const qs = new URLSearchParams();
    if (s) qs.set("start", s.toISOString().slice(0, 10));
    if (e) qs.set("end", e.toISOString().slice(0, 10));
    const q = qs.toString() ? `?${qs.toString()}` : "";
    api.get(`/reports/commercial${q}`).then((r) => setComm(r.data));
    api.get(`/reports/projects${q}`).then((r) => setProj(r.data));
    api.get(`/reports/collaborateurs${q}`).then((r) => setCollab(r.data));
  }, [period]);

  if (!comm || !proj || !collab || !time) return <Loader />;
  const pipelineData = Object.entries(comm.pipeline || {}).map(([name, value]) => ({ name, value }));

  const PERIODS = { all: "Toutes périodes", month: "Ce mois", quarter: "Ce trimestre", year: "Cette année" };
  const rangeFor = (p) => {
    const now = new Date(), y = now.getFullYear();
    if (p === "month") return [new Date(y, now.getMonth(), 1), new Date(y, now.getMonth() + 1, 0)];
    if (p === "quarter") { const q = Math.floor(now.getMonth() / 3); return [new Date(y, q * 3, 1), new Date(y, q * 3 + 3, 0)]; }
    if (p === "year") return [new Date(y, 0, 1), new Date(y, 11, 31)];
    return [null, null];
  };
  const iso = (d) => (d ? d.toISOString().slice(0, 10) : "");

  const exportDirectionPdf = async () => {
    try {
      const [s, e] = rangeFor(period);
      const qs = new URLSearchParams();
      if (s) qs.set("start", iso(s));
      if (e) qs.set("end", iso(e));
      qs.set("period", PERIODS[period]);
      const res = await api.get(`/reports/direction/pdf?${qs.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "rapport_direction.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error("Export PDF impossible"); }
  };

  return (
    <div>
      <PageHeader title="Rapports" subtitle="Outil de pilotage de la Direction — données consolidées">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44" data-testid="report-period"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(PERIODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={exportDirectionPdf} data-testid="export-direction-pdf" className="bg-brand-600 hover:bg-brand-700 text-white"><Download className="h-4 w-4 mr-1.5" />Exporter en PDF</Button>
      </PageHeader>
      <Tabs defaultValue="direction">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="direction" data-testid="tab-direction">Direction</TabsTrigger>
          <TabsTrigger value="commercial" data-testid="tab-report-commercial">Commercial</TabsTrigger>
          <TabsTrigger value="projets" data-testid="tab-report-projets">Projets</TabsTrigger>
          <TabsTrigger value="rh" data-testid="tab-report-rh">Collaborateurs</TabsTrigger>
          <TabsTrigger value="temps" data-testid="tab-report-temps">Temps</TabsTrigger>
        </TabsList>

        <TabsContent value="direction">
          <div className="space-y-6">
            <div><h3 className="font-head font-semibold mb-3">Commercial</h3>
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard label="Prospects" value={comm.prospects} icon={Users2} accent="blue" />
                <StatCard label="Opportunités" value={comm.opportunites} icon={Target} accent="cyan" />
                <StatCard label="Devis" value={comm.devis} icon={FileText} accent="amber" />
                <StatCard label="Contrats" value={comm.contrats} icon={FileSignature} accent="emerald" />
                <StatCard label="CA signé" value={fmtMoney(comm.chiffre_affaires_signe)} icon={Banknote} accent="emerald" />
              </div></div>
            <div><h3 className="font-head font-semibold mb-3">Projets internes</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Actifs" value={proj.actifs} icon={FolderKanban} accent="blue" />
                <StatCard label="Terminés" value={proj.termines} icon={CheckCircle2} accent="emerald" />
                <StatCard label="En retard" value={proj.retard} icon={AlertTriangle} accent="rose" />
                <StatCard label="Avancement moyen" value={`${proj.avancement_moyen}%`} icon={TrendingUp} accent="cyan" />
              </div></div>
            <div><h3 className="font-head font-semibold mb-3">Ressources humaines</h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Présents" value={collab.presents_aujourdhui} icon={CheckCircle2} accent="emerald" />
                <StatCard label="Absences" value={collab.absences} icon={Plane} accent="amber" />
                <StatCard label="Heures travaillées" value={`${collab.heures_travaillees}h`} icon={Clock} accent="blue" />
                <StatCard label="Heures sup." value={`${collab.heures_sup}h`} icon={Timer} accent="cyan" />
              </div></div>
            <div><h3 className="font-head font-semibold mb-3">Budget</h3>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard label="Budgets prévus" value={fmtMoney(proj.budget_prevu)} icon={Banknote} accent="blue" />
                <StatCard label="Budgets consommés" value={fmtMoney(proj.budget_consomme)} icon={Banknote} accent="amber" />
                <StatCard label="Restant" value={fmtMoney(proj.budget_prevu - proj.budget_consomme)} icon={Banknote} accent="emerald" />
              </div></div>
          </div>
        </TabsContent>

        <TabsContent value="commercial">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Taux conversion" value={`${comm.taux_conversion}%`} icon={TrendingUp} accent="emerald" />
            <StatCard label="Devis acceptés" value={comm.devis_acceptes} icon={FileText} accent="blue" />
            <StatCard label="Contrats" value={comm.contrats} icon={FileSignature} accent="cyan" />
            <StatCard label="CA signé" value={fmtMoney(comm.chiffre_affaires_signe)} icon={Banknote} accent="emerald" />
          </div>
          <div className="rounded-xl border bg-white p-5">
            <h3 className="font-head font-semibold mb-4">Pipeline</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={pipelineData} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={80} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip />
                <Bar dataKey="value" fill="#0066FF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="projets">
          <div className="rounded-xl border bg-white p-5">
            <h3 className="font-head font-semibold mb-4">Avancement des projets</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={proj.projets} layout="vertical" margin={{ left: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="nom" tick={{ fontSize: 11 }} width={140} />
                <Tooltip /><Bar dataKey="avancement" fill="#00B4D8" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="rh">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Collaborateurs" value={collab.collaborateurs} icon={Users2} accent="blue" />
            <StatCard label="Présents" value={collab.presents_aujourdhui} icon={CheckCircle2} accent="emerald" />
            <StatCard label="Heures travaillées" value={`${collab.heures_travaillees}h`} icon={Clock} accent="cyan" />
            <StatCard label="Heures sup." value={`${collab.heures_sup}h`} icon={Timer} accent="amber" />
            <StatCard label="Absences" value={collab.absences} icon={Plane} accent="amber" />
            <StatCard label="Retards (min)" value={collab.retards_min} icon={AlertTriangle} accent="rose" />
          </div>
        </TabsContent>

        <TabsContent value="temps">
          <div className="rounded-xl border bg-white overflow-hidden">
            <div className="p-4 border-b"><h3 className="font-head font-semibold">Temps par collaborateur / projet / tâche</h3></div>
            <Table>
              <TableHeader><TableRow className="bg-slate-50"><TableHead>Collaborateur</TableHead><TableHead>Projet</TableHead><TableHead>Tâche</TableHead><TableHead>Heures</TableHead></TableRow></TableHeader>
              <TableBody>
                {time.rows.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-8">Aucune donnée de temps</TableCell></TableRow> :
                  time.rows.map((r, i) => (
                    <TableRow key={i}><TableCell className="font-semibold">{r.collaborateur}</TableCell><TableCell>{r.projet || "—"}</TableCell><TableCell>{r.tache || "—"}</TableCell><TableCell className="font-mono font-bold">{r.heures} h</TableCell></TableRow>
                  ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
